import React, { useEffect, useState } from 'react'
import { Wrapper, CloseButton, AlertContent, IconWrapper, ProgressBar } from './alertElements'

interface AlertProps {
  color: string
  message: string
  show: boolean
  handleClose: () => void
  autoCloseDuration?: number // in milliseconds, default to 5000ms
}

const Alert = ({
  color,
  message,
  show,
  handleClose,
  autoCloseDuration = 5000,
}: AlertProps): React.ReactElement | null => {
  const [alertStyle, setAlertStyle] = useState({
    backgroundColor: '#f8f9fa',
    borderColor: '#dee2e6',
    textColor: '#212529',
    icon: '•',
  })

  const [progress, setProgress] = useState(100)

  useEffect(() => {
    switch (color) {
      case 'success':
        setAlertStyle({
          backgroundColor: '#f0fdf4',
          borderColor: '#22c55e',
          textColor: '#166534',
          icon: '✓',
        })
        break
      case 'error':
        setAlertStyle({
          backgroundColor: '#fef2f2',
          borderColor: '#ef4444',
          textColor: '#b91c1c',
          icon: '✕',
        })
        break
      case 'warning':
        setAlertStyle({
          backgroundColor: '#fffbeb',
          borderColor: '#f59e0b',
          textColor: '#92400e',
          icon: '⚠',
        })
        break
      case 'info':
        setAlertStyle({
          backgroundColor: '#eff6ff',
          borderColor: '#3b82f6',
          textColor: '#1e40af',
          icon: 'ℹ',
        })
        break
      default:
        setAlertStyle({
          backgroundColor: '#f8f9fa',
          borderColor: '#dee2e6',
          textColor: '#212529',
          icon: '•',
        })
        break
    }
  }, [color])

  useEffect(() => {
    if (show) {
      setProgress(100)
      const startTime = Date.now()

      const interval = setInterval(() => {
        const elapsedTime = Date.now() - startTime
        const remainingPercentage = 100 - (elapsedTime / autoCloseDuration) * 100
        setProgress(Math.max(remainingPercentage, 0))
      }, 16)

      const timer = setTimeout(() => {
        handleClose()
      }, autoCloseDuration)

      return () => {
        clearTimeout(timer)
        clearInterval(interval)
      }
    }
  }, [show, handleClose, autoCloseDuration])

  if (!show) return null

  return (
    <Wrapper
      style={{
        backgroundColor: alertStyle.backgroundColor,
        borderLeftColor: alertStyle.borderColor,
      }}
    >
      <AlertContent>
        <IconWrapper style={{ backgroundColor: alertStyle.borderColor }}>
          {alertStyle.icon}
        </IconWrapper>
        <span style={{ color: alertStyle.textColor }}>{message}</span>
      </AlertContent>
      <CloseButton
        onClick={handleClose}
        style={{ color: alertStyle.textColor }}
      >
        &times;
      </CloseButton>
      <ProgressBar
        style={{
          width: `${progress}%`,
          backgroundColor: alertStyle.borderColor,
        }}
      />
    </Wrapper>
  )
}

export default Alert
